/**
 * ProcessLog - CRUD for db_welding_procedure table
 * Styled to match WelderContinuityLog (toolbar, table, import/export).
 * Click a row to view its attached PDF. Upload PDFs via the attachment button.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const ProcessLog = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('processLog', 'global', true);
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProcess, setNewProcess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [importing, setImporting] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const pdfInputRef = useRef(null);

  const fetchProcedures = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welding-procedures');
      if (response.data.success) {
        setProcedures(response.data.data || []);
      }
    } catch (err) {
      setError('Failed to load welding procedures');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchProcedures(); }, [fetchProcedures]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleAdd = async () => {
    if (!newProcess.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/welding-procedures', { process: newProcess.trim() });
      if (response.data.success) {
        setProcedures(prev => [...prev, response.data.data]);
        setNewProcess('');
        setShowAddForm(false);
        setSuccessMsg('Procedure added');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add procedure');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editValue.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/welding-procedures/${id}`, { process: editValue.trim() });
      if (response.data.success) {
        setProcedures(prev => prev.map(p => p.id === id ? response.data.data : p));
        setEditingId(null);
        setEditValue('');
        setSuccessMsg('Procedure updated');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update procedure');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/welding-procedures/${id}`);
      if (response.data.success) {
        setProcedures(prev => prev.filter(p => p.id !== id));
        setSuccessMsg('Procedure deleted');
      }
    } catch (err) {
      setError('Failed to delete procedure');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welding-procedures/export', null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Process_Log.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/welding-procedures/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        setSuccessMsg(`Imported ${response.data.data.importedCount} procedures (${response.data.data.skippedCount} skipped)`);
        fetchProcedures();
      }
    } catch (err) {
      setError('Failed to import file');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welding-procedures/template', null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Process_Log_Template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleRowClick = async (proc) => {
    if (editingId === proc.id) return;
    if (!proc.pdf_filename) {
      setError(`No PDF attached to "${proc.process}". Use the attachment button to upload one.`);
      return;
    }
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/welding-procedures/${proc.id}/pdf`, null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setViewingPdf(proc);
    } catch (err) {
      setError('Failed to load PDF');
    }
  };

  const closePdfViewer = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setViewingPdf(null);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingId) return;
    try {
      setSaving(true);
      setError(null);
      const formData = new FormData();
      formData.append('pdf', file);
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/welding-procedures/${uploadingId}/pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        setProcedures(prev => prev.map(p => p.id === uploadingId ? { ...p, ...response.data.data } : p));
        setSuccessMsg('PDF uploaded successfully');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload PDF');
    } finally {
      setSaving(false);
      setUploadingId(null);
      e.target.value = '';
    }
  };

  const handleDeletePdf = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove the attached PDF?')) return;
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/welding-procedures/${id}/pdf`);
      if (response.data.success) {
        setProcedures(prev => prev.map(p => p.id === id ? { ...p, pdf_filename: null, pdf_original_name: null } : p));
        setSuccessMsg('PDF removed');
      }
    } catch (err) {
      setError('Failed to remove PDF');
    } finally {
      setSaving(false);
    }
  };

  const triggerPdfUpload = (id, e) => {
    e.stopPropagation();
    setUploadingId(id);
    setTimeout(() => pdfInputRef.current?.click(), 0);
  };

  const filtered = procedures.filter(p =>
    !search || p.process.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: '600', color: '#1e3a5f' }}>
        Process Log
      </h2>

      {/* Hidden PDF file input */}
      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf"
        onChange={handlePdfUpload}
        style={{ display: 'none' }}
      />

      {/* Toolbar */}
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search processes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '6px 12px', paddingLeft: '30px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '14px', width: '220px' }}
            />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}>&#128269;</span>
          </div>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {filtered.length} entries
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button onClick={() => setShowAddForm(!showAddForm)} style={{ padding: '8px 16px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
            {showAddForm ? '- Cancel' : '+ Add Entry'}
          </button>
          <button onClick={handleExport} style={{ padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
            Export
          </button>
          <label style={{ padding: '8px 16px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
            {importing ? 'Importing...' : 'Import'}
            <input type="file" accept=".xlsx,.xls" onChange={handleImport} style={{ display: 'none' }} disabled={importing} />
          </label>
          <button onClick={handleDownloadTemplate} style={{ padding: '8px 16px', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
            Template
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{ padding: '8px 16px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>&times;</button>
        </div>
      )}
      {successMsg && (
        <div style={{ padding: '8px 16px', backgroundColor: '#d1fae5', color: '#065f46', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
          {successMsg}
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600' }}>Add New Procedure</h3>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>Process</label>
              <input
                value={newProcess}
                onChange={(e) => setNewProcess(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                placeholder="e.g. WPS-140"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
              />
            </div>
            <button onClick={handleAdd} disabled={!newProcess.trim() || saving} style={{
              padding: '8px 20px', backgroundColor: !newProcess.trim() || saving ? '#93c5fd' : '#2563eb',
              color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
            }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Hint */}
      <div style={{ marginBottom: '12px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
        Click a row to view its attached PDF. Use the paperclip icon to attach/replace a PDF.
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#1e3a5f', color: 'white' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600' }}>Process</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600', width: '80px' }}>PDF</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', width: '180px' }}>Created At</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600', width: '220px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
                    {search ? 'No matching procedures found.' : 'No entries found. Click "Add Entry" to get started.'}
                  </td>
                </tr>
              ) : (
                filtered.map((proc, idx) => (
                  <tr
                    key={proc.id}
                    onClick={() => handleRowClick(proc)}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      borderLeft: '4px solid transparent',
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc',
                      cursor: proc.pdf_filename ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderLeftColor = '#dc2626';
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderLeftColor = 'transparent';
                      e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#f8fafc';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {editingId === proc.id ? (
                      <>
                        <td style={{ padding: '4px 12px' }}>
                          <input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleUpdate(proc.id); if (e.key === 'Escape') setEditingId(null); }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '100%', padding: '6px 10px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '13px' }}
                            autoFocus
                          />
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                          {proc.pdf_filename ? (
                            <span title={proc.pdf_original_name} style={{ color: '#dc2626', fontSize: '16px' }}>&#128196;</span>
                          ) : (
                            <span style={{ color: '#d1d5db' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 16px', color: '#6b7280' }}>{proc.created_at ? new Date(proc.created_at).toLocaleString() : '-'}</td>
                        <td style={{ padding: '4px 12px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => handleUpdate(proc.id)} disabled={saving} style={{ padding: '4px 10px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ padding: '4px 10px', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '10px 16px', fontWeight: '500' }}>{proc.process}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          {proc.pdf_filename ? (
                            <span title={proc.pdf_original_name || 'PDF attached'} style={{ color: '#dc2626', fontSize: '16px' }}>&#128196;</span>
                          ) : (
                            <span style={{ color: '#d1d5db', fontSize: '12px' }}>None</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', color: '#6b7280' }}>{proc.created_at ? new Date(proc.created_at).toLocaleString() : '-'}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                            <button onClick={(e) => { e.stopPropagation(); setEditingId(proc.id); setEditValue(proc.process); }} style={{ padding: '4px 10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>Edit</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(proc.id, proc.process); }} style={{ padding: '4px 10px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}>Del</button>
                            <button
                              onClick={(e) => triggerPdfUpload(proc.id, e)}
                              title={proc.pdf_filename ? 'Replace PDF' : 'Attach PDF'}
                              style={{ padding: '4px 8px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                            >
                              &#128206;
                            </button>
                            <button
                              onClick={(e) => proc.pdf_filename ? handleDeletePdf(proc.id, e) : e.stopPropagation()}
                              title="Remove PDF"
                              disabled={!proc.pdf_filename}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: proc.pdf_filename ? '#dc2626' : '#d1d5db',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: proc.pdf_filename ? 'pointer' : 'not-allowed',
                                fontSize: '14px',
                                opacity: proc.pdf_filename ? 1 : 0.5,
                              }}
                            >
                              &#128465;
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {viewingPdf && pdfUrl && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
          onClick={closePdfViewer}
        >
          <div
            style={{
              backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '900px',
              height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '16px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              backgroundColor: '#1e3a5f', color: 'white', borderRadius: '12px 12px 0 0',
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{viewingPdf.process}</h3>
                <span style={{ fontSize: '12px', opacity: 0.8 }}>{viewingPdf.pdf_original_name}</span>
              </div>
              <button
                onClick={closePdfViewer}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                  width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
                  fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                &times;
              </button>
            </div>
            {/* PDF Embed */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <iframe
                src={pdfUrl}
                title={`PDF - ${viewingPdf.process}`}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessLog;
